// @flow

import _ from 'lodash';

import getCookie from './getCookie';
import { createErrorType } from './error';
import { isBrowser } from './environment';

type FetchSuccess = Response & { responseText?: string };

export const NetworkError = createErrorType('NetworkError', {
  assign(error) {
    return {
      networkErrorMessage: error.message,
    };
  },
  summary(error) {
    return (
      "We're having some trouble connecting. You might try reloading the page or checking your internet connection. " +
      error.networkErrorMessage
    );
  },
});

const fetchSuccess = function(response: FetchSuccess) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }
  return response.text().then((body: string) => {
    // Hack to make legacy z.useful.errors work for now.
    response.responseText = body;
    throw response;
  });
};

// If the client doesn't already have a CSRF token, then retrieve one
const retrieveCsrfToken = () => {
  return !isBrowser() || getCookie('csrftoken')
    ? Promise.resolve()
    : fetch('/api/v3/csrf', { credentials: 'same-origin' })
        .then(fetchSuccess)
        .catch(error => {
          throw new NetworkError({
            ...error,
            message: 'Unable to retrieve CSRF token.',
          });
        });
};

/**
 * The CSRF token needs to be the first thing retrieved before any request can
 * take place. So we'll make sure this happens by caching this promise, all
 * requests should wait until that resolves before proceeding. CSRF token is
 * required for POST operations. POST is used by GraphQL for retreiving data.
 */
const _CSRF_TOKEN_READY: {| promise: ?Promise<?FetchSuccess> |} = {
  promise: null,
};

export const csrfTokenReady = () =>
  _CSRF_TOKEN_READY.promise ||
  (_CSRF_TOKEN_READY.promise = retrieveCsrfToken());

const csrfSafeMethod = function(method: string) {
  //these HTTP methods do not require CSRF protection
  return /^(GET|HEAD|OPTIONS|TRACE)$/.test(method);
};

const sameOrigin = function(url: string) {
  /* test that a given url is a same-origin URL
   * url could be relative or scheme relative or absolute
   * host + port
   */
  const host = document.location.host;
  const protocol = document.location.protocol;
  const sr_origin = '//' + host;
  const origin = protocol + sr_origin;

  /* Allow absolute or scheme relative URLs to same origin
   * or any other URL that isn't scheme relative or absolute i.e relative.
   */
  return (
    url === origin ||
    url.slice(0, origin.length + 1) === origin + '/' ||
    (url === sr_origin ||
      url.slice(0, sr_origin.length + 1) === sr_origin + '/') ||
    !/^(\/\/|http:|https:).*/.test(url)
  );
};

const wrappedFetch = function(url: RequestInfo, info?: RequestOptions) {
  info = info ? _.extend({}, info) : {};
  info.headers = info.headers || {};
  (info: Object).headers['X-Requested-With'] = 'XMLHttpRequest'; // help django's request.is_ajax()
  info.credentials = 'same-origin';

  const isCsrfSafeMethod = csrfSafeMethod(info.method || '');
  const preRequest = isCsrfSafeMethod ? Promise.resolve() : csrfTokenReady();

  return preRequest.then(() => {
    if (
      typeof url === 'string' &&
      isBrowser() &&
      !isCsrfSafeMethod &&
      sameOrigin(url)
    ) {
      // Always read from cookies in case a new CSRF token is set
      const token = getCookie('csrftoken');
      if (info && info.headers && token) {
        const headers = (info.headers: { [string]: string });
        headers['X-CSRFToken'] = token;
      }
    }

    return fetch.call(null, url, info).catch(error => {
      // Sentry captures this error:
      //
      // > We're having some trouble connecting. You might try reloading the page
      // > or checking your internet connection. Only absolute URLs are supported
      //
      // Let's try to include the URL so we can better understand the issue
      const message =
        error && _.isString(error.message) && typeof url === 'string'
          ? `${error.message} url='${url}'`
          : error && error.message;
      throw new NetworkError({ ...error, message });
    });
  });
};

wrappedFetch.success = fetchSuccess;

wrappedFetch.json = function(response): Promise<any> {
  return response
    .json()
    .then(data => {
      return data;
    })
    .catch(err => {
      // Empty content is expected with a 204 and is valid,
      // though parsing it as JSON would fail.
      if (response.status === 204) {
        return null;
      }
      // Throw the error if it was a problem parsing.
      if (response.status >= 200 && response.status < 300) {
        throw err;
      }
      // Otherwise, maybe this was an error status and JSON wasn't expected.
      throw response;
    });
};

export const fetchJson = (uri: string, info?: RequestOptions) => {
  info = info ? _.extend({}, info) : {};
  info.headers = info.headers || {};
  (info: Object).headers['Content-Type'] = 'application/json';
  (info: Object).headers.Accept = 'application/json';

  return wrappedFetch(uri, info)
    .then(wrappedFetch.success)
    .then(wrappedFetch.json);
};

// `fetchJson` via `method`, passing along a stringified
// `body` if it exists.
const fetchJsonWithMethod = (method, url, body) =>
  fetchJson(url, {
    ...(method !== 'GET' ? { body: body && JSON.stringify(body) } : {}),
    method,
  });

export const deleteJson = _.partial(fetchJsonWithMethod, 'DELETE');
export const getJson = _.partial(fetchJsonWithMethod, 'GET');
export const patchJson = _.partial(fetchJsonWithMethod, 'PATCH');
export const postJson = _.partial(fetchJsonWithMethod, 'POST');
export const putJson = _.partial(fetchJsonWithMethod, 'PUT');

export default wrappedFetch;
